<template>
   <RequestLoader
      data-cy="flow-end-request-loader"
      :isLoading="state.isFetchingData"
      :error="loadErrors"
      has-contact-button
      @retry="completeFlow"
   >
      <div v-if="state.endData" data-cy="block-flow-end" class="flex size-full flex-col justify-center py-4">
         <section class="mx-auto max-w-md">
            <h2 v-if="submissionText?.heading" data-cy="flowend-heading" class="text-2xl leading-tight lg:text-3xl">
               {{ submissionText.heading }}
            </h2>
            <RichTextRenderer
               v-if="submissionText?.content"
               data-cy="flowend-text"
               class="mt-6 text-lg"
               :document="submissionText.content"
            />

            <DefaultButton class="mx-auto mt-8" data-cy="end-block-continue-btn" @click="goToEndUrl">
               {{ $t("survey.button_continue") }}
            </DefaultButton>
         </section>
      </div>
   </RequestLoader>
</template>

<script setup lang="ts">
import { useUserStore } from "@/stores/userStore";
import { useAuthStore } from "@/stores/authStore";
import { useRouteLocale } from "@/composables/useRouteLocale";
import { useRoute } from "vue-router";
import type { RichText } from "@/types";
import type { AxiosError } from "axios";
import { addLocaleToRedirectPath } from "@/router/routes-config";
import { trackStudyJoinState } from "@/js/services/metaPixel.service";
import FlowsApi, { type FlowStudy } from "@/data/api-layer/flowsApi";
import { useFlowParticipationStore } from "@/stores/flowParticipationStore";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";

interface State {
   isFetchingData: boolean;
   // similar to FlowParticipationModel
   endData: {
      flowId: string;
      isEligible: boolean;
      lastSubmittedBlockId: string;
      reachedEndAt: string;
      screenoutReason?: object;
   };
   endRules?: {
      type: string;
      value: string;
   };
}

export interface FlowselEndBlock extends FlowselBlock {
   data: {
      screenoutEnd: {
         heading: string;
         content: RichText;
      };
      successfulEnd: {
         heading: string;
         content: RichText;
      };
   };
}

// declarations
// ------------------------------
const userStore = useUserStore();
const authStore = useAuthStore();
const i18n = useI18n();
const flowParticipationStore = useFlowParticipationStore();
const $route = useRoute();
const { localePath } = useRouteLocale();

const props = defineProps<{
   block: FlowselEndBlock;
   flowStudy?: FlowStudy;
}>();

// state
const loadErrors = ref<Error[]>([]);
const state: State = reactive<State>({
   isFetchingData: true,
   endData: null,
   endRules: null,
});

// lifecycle
// ------------------------------
onMounted(async () => {
   await completeFlow();
});

// Computed
// ------------------------------
// submission heading & content
const submissionText = computed(() => {
   if (!state.endData) return null;
   const blockInfo = props.block.data;

   // if the text for screenout and successful end are not set, return null
   if (
      !blockInfo?.successfulEnd?.heading &&
      !blockInfo?.screenoutEnd?.heading &&
      !blockInfo?.successfulEnd?.content &&
      !blockInfo?.screenoutEnd?.content
   ) {
      return null;
   }

   // Otherwise return the appropriate heading and text content
   const submissionText = {
      heading: state.endData.isEligible ? blockInfo.successfulEnd.heading : blockInfo.screenoutEnd.heading,
      content: state.endData.isEligible ? blockInfo.successfulEnd.content : blockInfo.screenoutEnd.content,
   };

   return submissionText;
});

const endUrl = computed(() => {
   if (state.endRules?.type === "go_to_flow") {
      return `${localePath.value}/flow/${state.endRules?.value}`;
   } else if (state.endRules?.type === "go_to_url") {
      return state.endRules?.value;

      // default continue link is the home page if logged-in, otherwise to landing page
   } else if (authStore.authTokenExists) {
      return `${localePath.value}/home`;
   }
   return `${localePath.value}/`;
});

// Methods
// ------------------------------
async function completeFlow() {
   try {
      state.isFetchingData = true;
      // POST complete flow, this will give us a result which we can use to display the relevant data
      let result;
      const isAnonymousCompletion = flowParticipationStore.token; // anonymous flow completion requires a token

      if (isAnonymousCompletion) {
         result = await FlowsApi.completeFlowAnonymously(props.block.flowId, flowParticipationStore.token);
      } else {
         result = await FlowsApi.completeFlow(props.block.flowId);
      }

      state.endData = result.data.data;
      state.endRules = result.data.endRulesResult;

      // if the portal is using the core profile feature, we redirect immediately to the requested destination or home
      if (props.block.flowId === "coreprofile") {
         instantRedirectForCoreProfile(i18n);
      }

      checkAnalyticsTracking();
   } catch (error) {
      console.error("Failed on complete-flow > ", error);

      loadErrors.value = [error as AxiosError];
   } finally {
      state.isFetchingData = false;
   }
}

function checkAnalyticsTracking() {
   // If the Survey is the study joining flow, track the meta pixel even (if using)
   if (props.flowStudy?.joiningFlowId !== props.block.flowId) return;

   // if the user is eligible, track the meta pixel event for joined study
   if (state.endData.isEligible) {
      trackStudyJoinState(props.flowStudy.id, "joined");
   }
   // if the user is not eligible, track the meta pixel event for screenout
   else {
      trackStudyJoinState(props.flowStudy.id, "screenout");
   }
}

/** goes to the URL specified by the BE (ex. either study landing page, next flow etc.) */
function goToEndUrl() {
   window.open(endUrl.value, "_self");
}

// If this is a the core profile flow, we redirect immediately to the requested destination or home
// ❗ Note that we do not use the CMS Continue button for this flow
// This could be refactored once we create a Core Profile page (ideally this component should not have flow specific code)
function instantRedirectForCoreProfile(i18n) {
   // since the flow has been completed, set the user store core profile flow to completed
   userStore.isCoreProfileFlowComplete = true;

   // check if the 'redirect' param is set
   const { redirect, ...params }: { redirect?: string } = $route.query;

   // if it's the core-profile flow and the redirect param is set apply the redirect (carry all other params)
   if (redirect) {
      const queryString = new URLSearchParams(params).toString();

      window.open(addLocaleToRedirectPath(`${redirect}?${queryString}`, i18n), "_self");
   }
   // default to Home
   else {
      window.open(`${localePath.value}/home`, "_self");
   }
}
</script>
